import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { ChevronDown, Users, Check } from 'lucide-react'
import { Button } from './ui/button'

interface TeamSwitcherProps {
  currentTeamId?: string
  currentTeamName?: string
}

export const TeamSwitcher: React.FC<TeamSwitcherProps> = ({ 
  currentTeamId, 
  currentTeamName 
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { userTeams } = useAuth()
  const navigate = useNavigate()
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleTeamSelect = (teamId: string) => {
    setIsOpen(false)
    navigate(`/dashboard/team/${teamId}`)
  }

  const teams = userTeams || []
  
  // Don't render if user has no teams or only one team
  if (teams.length <= 1) {
    return (
      <div className="flex items-center space-x-2 px-4 py-2 rounded-full bg-accent/40 border border-border/30 text-muted-foreground">
        <Users className="h-4 w-4" />
        <span className="font-medium text-sm">{currentTeamName || 'Team'}</span>
      </div>
    )
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        type="button"
        variant="ghost"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-4 py-2 h-auto rounded-full bg-accent/40 border border-border/30 text-muted-foreground hover:bg-accent/60 hover:text-foreground transition-all duration-300 hover:shadow-sm"
      >
        <Users className="h-4 w-4" />
        <span className="font-medium text-sm">
          {currentTeamName || 'Select Team'}
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      {isOpen && (
        <div className="absolute right-0 top-full z-50 mt-2 w-64 border border-border bg-background py-2 shadow-sm">
          <div className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Your Teams
          </div>
          
          {teams.map((membership) => {
            const team = membership.teams
            const isCurrentTeam = team?.id === currentTeamId
            
            return (
              <Button
                key={membership.id}
                type="button"
                variant="ghost"
                onClick={() => team?.id && handleTeamSelect(team.id)}
                className={`w-full h-auto justify-between text-left px-3 py-2 rounded-none hover:bg-accent transition-colors flex items-center ${
                  isCurrentTeam ? 'bg-secondary text-secondary-foreground' : 'text-foreground'
                }`}
              >
                <div className="flex items-center space-x-3">
                  {/* Team avatar: rounded-md square, per UI_STYLE_GUIDE.md §9 --
                      teams/pods are square, people stay circular. */}
                  <div className={`h-8 w-8 rounded-md flex items-center justify-center text-xs font-medium ${
                    isCurrentTeam
                      ? 'bg-secondary text-secondary-foreground'
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    {team?.name?.charAt(0).toUpperCase() || 'T'}
                  </div>
                  <div>
                    <div className="font-medium">{team?.name || 'Unknown Team'}</div>
                    <div className="text-xs text-muted-foreground capitalize">{membership.role}</div>
                  </div>
                </div>

                {isCurrentTeam && (
                  <Check className="h-4 w-4 text-secondary-foreground" />
                )}
              </Button>
            )
          })}

          <div className="border-t border-border mt-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setIsOpen(false)
                navigate('/dashboard')
              }}
              className="w-full h-auto justify-start rounded-none text-left px-3 py-2 text-sm text-muted-foreground hover:bg-accent transition-colors"
            >
              ← Back to All Teams
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default TeamSwitcher
